import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLoading } from '../../context/LoadingContext';
import { useForgotPassword } from '../../context/ForgotPasswordContext';
import toast from 'react-hot-toast';
import { EnvelopeIcon,KeyIcon  } from '@heroicons/react/24/outline';

const schema = yup.object().shape({
  email: yup.string().email('Invalid email format').required('Email is required')
});

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { forgotPassword,formErrors } = useForgotPassword();
  const { register, handleSubmit, setError, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });
  const { showLoading, hideLoading } = useLoading();

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();

    const response = await forgotPassword(data);

    if (response.status === 200) {
      toast.success('Password reset instructions have been sent to your email');
      navigate('/login');
    } else {
      if (response.errors) {
        Object.entries(response.errors).forEach(([field, message]) => {
          setError(field, {
            type: 'manual',
            message,
          });
        });
      } else if (response.error?.message) {
        setError('general', {
          type: 'manual',
          message: response.error.message,
        });
      }
    }
    setIsLoading(false);
    hideLoading();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#009efb] to-[#0089db] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-2xl">
         
        <div>
          <div className="flex justify-center">
            <div className="h-12 w-12 bg-[#009efb] rounded-lg flex items-center justify-center">
              
              <KeyIcon className="h-5 w-5 stroke-[2.5] text-white"/>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Forgot Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you instructions to reset your password
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400"/>
                </div>
                <input
                  id="email"
                  type="email"
                  {...register('email')}
                  className={`appearance-none block w-full pl-10 px-3 py-2 border ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email?.message}</p>
              )}
            </div>
          </div>

          {errors.general && (
            <div className="text-red-600 text-sm mt-2">{errors.general.message}</div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200 ${
                isLoading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </div>
        </form>

        {formErrors?.general && (
            <div className="text-red-600 text-sm mt-2">{formErrors.general}</div>
        )}

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Remember your password?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="font-medium text-[#009efb] hover:text-[#0089db]"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 